import type { MessageKey } from '../i18n/messages';

/**
 * Перевод отказа проверки тела запроса в сообщение нашим словарём (AUTH-14).
 *
 * <p><b>Зачем.</b> Раньше сервер отдавал в поле `error` текст Hibernate Validator, и язык этого
 * текста выбирал сам валидатор по заголовку `Accept-Language` запроса. В логах прода за 11–12
 * сентября 2026 видно, чем это кончается: ревьюер Apple получал `个数必须在2和64之间` и
 * `不能为空` — в приложении, где четыре языка и китайского среди них нет. Теперь сервер отдаёт
 * `field` и `rule`, а слова подбираются здесь.
 *
 * <p><b>Почему сообщения общие, а не по каждому полю.</b> Соответствие «поле × правило →
 * фраза» — это десятки строк на каждый из четырёх языков, и устаревать они будут молча: поле
 * переименуют на сервере, а словарь об этом не узнает. Здесь сознательно выбрана честная
 * середина: правило переводится точно, поле — нет. Подробные подсказки живут там, где они
 * уместны и проверяемы, — в проверках самой формы (`validateLoginForm`, `validateRegistration`),
 * которые срабатывают раньше и до сети вообще не доходят. Этот перевод — сетка безопасности
 * для случаев, до которых форма не додумалась, а не основной способ разговора с человеком.
 */

/** Тело отказа, как его отдаёт `ApiExceptionHandler.handleValidation`. */
export type ValidationErrorBody = {
  error?: string;
  field?: string;
  rule?: string;
};

/**
 * Правило нарушено → какой ключ показать.
 *
 * <p>`Email` отдельной строкой, потому что это единственное правило, где общая фраза заметно
 * хуже частной: «проверьте адрес» человек понимает сразу, а «недопустимый формат» — нет.
 */
function keyForRule(rule: string): MessageKey {
  switch (rule) {
    case 'NotBlank':
    case 'NotNull':
    case 'NotEmpty':
      return 'validationFieldRequired';
    case 'Email':
      return 'registerEmailInvalid';
    case 'Size':
    case 'Min':
    case 'Max':
      return 'validationFieldLength';
    case 'Pattern':
      return 'validationFieldFormat';
    default:
      return 'validationFailed';
  }
}

/**
 * Ключ сообщения для тела отказа — или `null`, если это не отказ проверки.
 *
 * <p>`null`, а не запасной ключ: у вызывающего есть собственный запасной текст, привязанный к
 * действию («не удалось загрузить список»), и он точнее, чем «проверьте введённые данные» на
 * ответе, который к валидации отношения не имеет.
 */
export function validationMessageKey(body: ValidationErrorBody | null | undefined): MessageKey | null {
  const rule = body?.rule;
  if (typeof rule !== 'string' || rule.length === 0) {
    return null;
  }
  // Адрес почты узнаётся и по имени поля: на `@Pattern` у поля `email` правило приедет как
  // `Pattern`, а сказать про адрес всё равно уместнее, чем про «недопустимые символы».
  if (typeof body?.field === 'string' && body.field.toLowerCase().includes('email')) {
    return 'registerEmailInvalid';
  }
  return keyForRule(rule);
}
